import { whatsappUrl } from "../../shared/whatsapp";
import { isCardCategory, variantLabel, type VariantKey } from "../../shared/variants";
import { useI18n } from "../i18n/I18nProvider";
import { gaPricer, track } from "../lib/analytics";
import { useCatalog } from "../lib/CatalogProvider";
import { useMoney } from "../lib/CurrencyProvider";
import { WhatsAppIcon } from "./WhatsAppBubble";

interface Props {
  product: { sku: string; name: { es: string; en: string }; category: string; slug: string };
  /** The version on screen (condition, finish, language and its price). */
  variant: (VariantKey & { price: number; priceArs?: number | null; sku: string }) | null;
  price: number;
  priceArs: number | null | undefined;
  quantity: number;
  soldOut: boolean;
}

/**
 * "Comprar por WhatsApp" on the product page: a wa.me chat with the product, the version, the
 * quantity and the price on screen already written, so the question arrives with context. Out of
 * stock it asks whether it's coming back. Hidden until the shop's number is set.
 */
export function WhatsAppBuyButton({ product, variant, price, priceArs, quantity, soldOut }: Props) {
  const { t, pick, locale } = useI18n();
  const { settings } = useCatalog();
  const money = useMoney();
  const phone = settings.whatsapp ? pick(settings.whatsapp) : "";
  const name = pick(product.name);
  const version = variant && isCardCategory(product.category) ? ` (${variantLabel(variant, locale)})` : "";
  const link = `${window.location.origin}/producto/${encodeURIComponent(product.slug)}`;
  const amount = money.format(price, priceArs);
  const text = soldOut
    ? t("whatsapp.askStock", { product: `${name}${version}`, link })
    : quantity > 1
      ? t("whatsapp.buyMany", { quantity, product: `${name}${version}`, price: amount, link })
      : t("whatsapp.buy", { product: `${name}${version}`, price: amount, link });
  const url = whatsappUrl(phone, text);
  if (!url) return null;

  const onClick = () => {
    // A lead, not a sale: in Google Ads it can only be a secondary conversion.
    const ga = gaPricer(money);
    const unit = ga.price(price, priceArs);
    track("whatsapp_click", {
      currency: ga.currency,
      value: soldOut ? 0 : unit * quantity,
      items: [{ item_id: variant?.sku ?? product.sku, item_name: product.name.es, price: unit, quantity, item_category: product.category }],
    });
  };

  return (
    <a className="btn btn-lg btn-whatsapp" href={url} target="_blank" rel="noopener noreferrer" onClick={onClick}>
      <WhatsAppIcon size={22} />
      {soldOut ? t("whatsapp.askStockButton") : t("whatsapp.buyButton")}
    </a>
  );
}
